/**
 * Usage-billing issues — the ledger write behind every AI, tool and phone charge.
 */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";

export const BILLING_ISSUES = {
  /**
   * A charge did not land: the transaction that moves the organization's balance
   * and writes its `TokenUsage` line item rolled back, so that usage is recorded
   * NOWHERE — not in the balance, not in the usage analytics, not on an invoice.
   *
   * One row per organization. A charge failure is an EVENT, not a standing
   * condition, so nothing resolves this row on its own: a later successful charge
   * proves nothing about the surface that failed, and resolving on one would turn
   * a per-surface defect into an open/resolve flap that pages on every reopen. The
   * row counts every further failure while open; an operator resolves it once the
   * cause is fixed, and a failure after that reopens it.
   */
  BILLING_USAGE_CHARGE_FAILED: {
    category: ISSUE_CATEGORY.BILLING,
    message:
      "A usage charge for this organization did not land: its balance did not move and no TokenUsage row was written",
    severity: "high",
    fault: ISSUE_FAULT.NEXUS,
    // Pages: every further failure is usage that is lost for good, and the last
    // defect of this shape ran for 97 days because nobody was told.
    page: true,
    remedy:
      "The usage that failed was charged to nobody and is not in the usage analytics. rawPayload carries the attempted usages (`type:id`), the Prisma error code and its meta.\n" +
      "1. Read the error code first. P2003 is a foreign key refusing the TokenUsage insert — almost always an id from the wrong table (a correlation id passed as a `runId`, say), so find the caller that built that usage. P2025 is a missing organization or tool row.\n" +
      "2. Sentry, tagged `surface: billing.saveTokenUsages`, carries the stack, which is the only place that says which write refused.\n" +
      "3. The `billing_charge_failures_total` counter gives the rate: whether this is still happening and whether it is getting worse.\n" +
      "Resolve this issue by hand once the cause is fixed. It does not resolve itself, because a charge failure is an event and a later success proves nothing about the surface that failed."
  }
} as const satisfies Record<string, IssueDefinition>;
